if (typeof ProductReviewStats === 'undefined') {
  class ProductReviewStats {
    constructor() {
      this.statsCache = new Map();
      this.mainModelCode = null;
      this.mainGlobalsInitialized = false;
    }

    async extractModelCode() {
      if (window.reviewsTranslations && window.reviewsTranslations.modelCode) {
        return window.reviewsTranslations.modelCode;
      }
      return null;
    }

    async fetchStats(modelCode) {
      if (!modelCode || !window.reviewsApiUrl) return null;

      if (this.statsCache.has(modelCode)) {
        return this.statsCache.get(modelCode);
      }

      const url = `${window.reviewsApiUrl}/reviews/model/${modelCode}/only_stats`;
      
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        this.statsCache.set(modelCode, data);
        return data;
      } catch (error) {
        console.error('Failed to fetch review stats:', error);
        return null;
      }
    }

    updateContainer(container, stats) {
      if (!stats || !container) return;

      const { averageRating, count } = stats;
      
      const starsContainer = container.querySelector('[data-review-stars]');
      if (starsContainer) {
        this.updateStars([starsContainer], averageRating);
      }

      const ratingValue = container.querySelector('[data-review-rating]');
      if (ratingValue) {
        ratingValue.forEach ? ratingValue.forEach(el => el.textContent = this.formatRating(averageRating)) : ratingValue.textContent = this.formatRating(averageRating);
      }

      const reviewCount = container.querySelector('[data-review-count]');
      if (reviewCount) {
        const currentText = reviewCount.textContent.trim();
        const formattedCount = this.formatNumber(count);
        reviewCount.textContent = /\d+/.test(currentText) ? currentText.replace(/\d+/, formattedCount) : `${formattedCount} ${currentText}`;
      }

      container.style.display = '';
      container.setAttribute('data-stats-initialized', 'true');
    }

    updateMainProductGlobals(stats) {
      if (this.mainGlobalsInitialized) return;

      const { count, ratingDistribution } = stats;

      const reviewTabs = document.querySelectorAll('.reviews_tab');
      reviewTabs.forEach(tab => {
        tab.style.display = '';
        const tabText = tab.textContent.trim();
        if (/\d+/.test(tabText)) {
          const formattedCount = this.formatNumber(count);
          tab.textContent = tabText.replace(/\d+/, formattedCount);
        }
      });

      if (ratingDistribution) {
        this.updateDistribution(ratingDistribution, count);
      }

      const reviewTitles = document.querySelectorAll('.ReviewDetails-title');
      reviewTitles.forEach(title => title.style.display = '');

      this.mainGlobalsInitialized = true;
    }

    hideMainProductGlobals() {
      const reviewTabs = document.querySelectorAll('.reviews_tab');
      reviewTabs.forEach(tab => tab.style.display = 'none');
      
      const reviewTitles = document.querySelectorAll('.ReviewDetails-title');
      reviewTitles.forEach(title => title.style.display = 'none');
    }

    updateDistribution(ratingDistribution, totalCount) {
      const distributionContainers = document.querySelectorAll('[data-review-distribution]');
      
      distributionContainers.forEach(container => {
        container.style.display = 'flex';
        
        ratingDistribution.forEach(rating => {
          const starCode = rating.code;
          const row = container.querySelector(`[data-star="${starCode}"]`);
          
          if (row) {
            const percentage = totalCount > 0 ? (rating.value / totalCount * 100).toFixed(1) : 0;
            const fillBar = row.querySelector('.distribution-fill');
            const countSpan = row.querySelector('.distribution-count');
            
            if (fillBar) {
              fillBar.style.width = `${percentage}%`;
            }
            if (countSpan) {
              const formattedCount = this.formatNumber(rating.value);
              countSpan.textContent = formattedCount;
            }
          }
        });
      });
    }

    formatNumber(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    formatRating(rating) {
      const rounded = Math.round(rating * 10) / 10;
      return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
    }

    updateStars(starsContainers, rating) {
      const maxStars = 5;
      const fullStars = Math.floor(rating);
      const decimal = rating - fullStars;
      const hasHalfStar = decimal >= 0.3 && decimal <= 0.7;

      starsContainers.forEach(container => {
        let starsHtml = '';
        
        for (let i = 1; i <= maxStars; i++) {
          if (i <= fullStars) {
            starsHtml += `<svg fill="none" focusable="false" width="14" height="14" class="icon icon--rating-star rating__star rating__star--full" viewBox="0 0 14 13"><path d="M7 0L8.6458 4.73475L13.6574 4.83688L9.66296 7.86525L11.1145 12.6631L7 9.8L2.8855 12.6631L4.33704 7.86525L0.342604 4.83688L5.3542 4.73475L7 0Z" fill="currentColor"></path></svg>`;
          } else if (i === fullStars + 1 && hasHalfStar) {
            starsHtml += `<svg fill="none" focusable="false" width="14" height="14" class="icon icon--rating-star-half rating__star rating__star--half" viewBox="0 0 14 13"><path d="M7 0L8.6458 4.73475L13.6574 4.83688L9.66296 7.86525L11.1145 12.6631L7 9.8V0Z" fill="currentColor"></path></svg>`;
          } else {
            starsHtml += `<svg fill="none" focusable="false" width="14" height="14" class="icon icon--rating-star rating__star rating__star--empty" viewBox="0 0 14 13"><path d="M7 0L8.6458 4.73475L13.6574 4.83688L9.66296 7.86525L11.1145 12.6631L7 9.8L2.8855 12.6631L4.33704 7.86525L0.342604 4.83688L5.3542 4.73475L7 0Z" fill="currentColor"></path></svg>`;
          }
        }
        
        container.innerHTML = starsHtml;
      });
    }

    async init() {
      const containers = document.querySelectorAll('[data-review-container]:not([data-stats-initialized])');
      const containersBySku = new Map();
      
      // Get main model code (async)
      const mainModelCode = await this.extractModelCode();

      containers.forEach(container => {
        let sku = container.dataset.sku;
        // Fallback to mainModelCode if sku is missing
        if (!sku && mainModelCode) {
          sku = mainModelCode;
        }
        
        if (sku) {
          if (!containersBySku.has(sku)) {
            containersBySku.set(sku, []);
          }
          containersBySku.get(sku).push(container);
        }
      });

      // Fetch and update
      for (const [sku, skuContainers] of containersBySku) {
        const stats = await this.fetchStats(sku);
        
        if (stats && stats.count > 0) {
          skuContainers.forEach(container => this.updateContainer(container, stats));
          
          // If this is the main product, update global elements
          if (mainModelCode && sku == mainModelCode) {
            this.updateMainProductGlobals(stats);
          }
        } else {
          skuContainers.forEach(container => container.style.display = 'none');
        }
      }

      // Handle case where main product has no containers but has globals
      if (mainModelCode && !containersBySku.has(mainModelCode) && !this.mainGlobalsInitialized) {
        const stats = await this.fetchStats(mainModelCode);
        if (stats && stats.count > 0) {
          this.updateMainProductGlobals(stats);
        } else {
          this.hideMainProductGlobals();
        }
      }
    }
  }

  window.ProductReviewStats = ProductReviewStats;
}

if (typeof window.productReviewStatsInstance === 'undefined') {
  window.productReviewStatsInstance = new ProductReviewStats();
  
  const initStats = () => {
    window.productReviewStatsInstance.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStats);
  } else {
    initStats();
  }
}
